import React from "react";
import {
  BarSeries,
  LineSeries,
  GlyphSeries,
  AreaSeries,
  BarStack,
} from "@visx/xychart";
import { LinearGradient } from "@visx/gradient";
import type { ChartConfig } from "../../types";
import type { ProcessedSeriesData } from "../../hooks/useChartData";

// Accessor functions
const xAccessor = (d: { x: any; y: any }) => d?.x;
const yAccessor = (d: { x: any; y: any }) => d?.y;

// Map series config to color
const getColor = (
  config: ChartConfig,
  seriesKey: string,
  index: number
): string => {
  const seriesConfig = config.series?.find((s) => s.key === seriesKey);
  if (seriesConfig?.color) {
    return seriesConfig.color;
  }
  const defaultColors = [
    "#2196f3",
    "#4caf50",
    "#ff9800",
    "#e91e63",
    "#9c27b0",
    "#795548",
    "#607d8b",
  ];
  return defaultColors[index % defaultColors.length];
};

/**
 * Renders individual series components (Bar, Line, Scatter).
 * Stacked bars are handled using BarStack component.
 */
export const renderSeries = (
  config: ChartConfig,
  seriesData: ProcessedSeriesData[]
) => {
  if (!config || !config.series || !seriesData) return null;

  const getSeriesConfig = (key: string) =>
    config.series?.find((s) => s.key === key);

  // Group series by type to separate stacked-bar series
  const stackedBarSeries = seriesData.filter(
    (s) => getSeriesConfig(s.key)?.type === "stacked-bar"
  );

  const regularSeries = seriesData.filter(
    (s) => getSeriesConfig(s.key)?.type !== "stacked-bar"
  );

  return (
    <>
      {/* Define Gradients First */}
      {config.series
        .filter((sc) => sc.type === "line" && sc.areaGradient)
        .map((seriesConfig, index) => {
          const originalIndex =
            config.series?.findIndex((s) => s.key === seriesConfig.key) ?? -1;
          const color = getColor(
            config,
            seriesConfig.key,
            originalIndex >= 0 ? originalIndex : index
          );
          const gradientId = `area-gradient-${seriesConfig.key}`;
          return (
            <LinearGradient
              key={`grad-${seriesConfig.key}`}
              id={gradientId}
              from={color}
              to={color}
              fromOpacity={0.3}
              toOpacity={0}
            />
          );
        })}

      {/* Render Stacked Bars if any */}
      {stackedBarSeries.length > 0 && (
        <BarStack>
          {stackedBarSeries
            .filter((series) => getSeriesConfig(series.key))
            .map((series, index) => {
              const originalIndex =
                config.series?.findIndex((s) => s.key === series.key) ?? -1;
              const color = getColor(
                config,
                series.key,
                originalIndex >= 0 ? originalIndex : index
              );

              return (
                <BarSeries
                  key={series.key}
                  dataKey={series.key}
                  data={series.data}
                  xAccessor={xAccessor}
                  yAccessor={yAccessor}
                  colorAccessor={() => color}
                />
              );
            })}
        </BarStack>
      )}

      {/* Render Regular Series */}
      {regularSeries.map((series, index) => {
        const seriesConfig = getSeriesConfig(series.key);
        if (!seriesConfig) return null;

        const originalIndex =
          config.series?.findIndex((s) => s.key === series.key) ?? -1;
        const color = getColor(
          config,
          series.key,
          originalIndex >= 0 ? originalIndex : index
        );
        const gradientId = `area-gradient-${series.key}`;

        switch (seriesConfig.type) {
          case "bar":
            return (
              <BarSeries
                key={series.key}
                dataKey={series.key}
                data={series.data}
                xAccessor={xAccessor}
                yAccessor={yAccessor}
                colorAccessor={() => color}
              />
            );
          case "line":
            return (
              <React.Fragment key={series.key}>
                {seriesConfig.areaGradient && (
                  <AreaSeries
                    dataKey={`${series.key}-area`}
                    data={series.data.filter((d) => d.y !== null)}
                    xAccessor={xAccessor}
                    yAccessor={yAccessor}
                    fill={`url(#${gradientId})`}
                    renderLine={false}
                    curve={seriesConfig.curve}
                  />
                )}
                <LineSeries
                  dataKey={series.key}
                  data={series.data.filter((d) => d.y !== null)}
                  xAccessor={xAccessor}
                  yAccessor={yAccessor}
                  stroke={color}
                  strokeWidth={seriesConfig.strokeWidth ?? 2}
                  strokeDasharray={seriesConfig.strokeDasharray}
                  curve={seriesConfig.curve}
                />
              </React.Fragment>
            );
          case "scatter":
            return (
              <GlyphSeries
                key={series.key}
                dataKey={series.key}
                data={series.data}
                xAccessor={xAccessor}
                yAccessor={yAccessor}
                colorAccessor={() => color}
                size={config.glyphSize || 8}
              />
            );
          default:
            console.warn(
              `Unsupported series type: ${seriesConfig.type} for key: ${series.key}`
            );
            return null;
        }
      })}
    </>
  );
};
